import * as R from 'ramda'
import {
  CreateInvitation,
  CreateReferrerCodes,
  FullCode,
  FullCodeOptions,
  GetInvitations,
  GetReferrerCodes,
  reqCreateInvitation,
  reqCreateReferrerCodes,
  reqGetInvitations,
  reqGetReferrerCodes,
  reqSetUserInformation,
  ResGetInvitationsPayload,
  SetUserInformation,
} from '@weblite/ts-action/invitation'
import { type ResponseAction } from '@weblite/ts-action/types'
import {
  BatchSignup,
  BatchSignupItem,
  reqBatchSignup,
  ResBatchSignupPayload,
  User,
} from '@weblite/ts-action/user'
import { styled } from '@mui/material/styles'
import Button from '@mui/material/Button'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Link,
  ListItem,
  Stack,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import readXlsxFile, { readSheetNames } from 'read-excel-file'

import { showSaveChangesLoadingModal } from '$comp/modals/loading-modal'
import { request } from '$setup/ws'
import { NumberInput } from '$common/input'
import { showErrorSnackbar } from '$comp/snackbars'
import hostConf from '$config/frontend'
import { useClientUser } from '$logic/client-user'
import { getUser, serverGetUserByPhone } from '$logic/users'

const wrapLoading =
  <T, A extends unkown[]>(fn: (...args: A) => Promise<T>) =>
  async (...args: A) => {
    showSaveChangesLoadingModal()
    const result = await fn(...args)
    await closeCurrentModal()
    return result
  }

// arabic and persian
const ar = '٠١٢٣٤٥٦٧٨٩۰۱۲۳۴۵۶۷۸۹'.split('')
const en = '01234567890123456789'.split('')
export const normalizePhone = (phone: string, defaultCountryCode = '+98') => {
  let trimmedPhone = phone?.trim()
  if (!trimmedPhone) return ''

  trimmedPhone = trimmedPhone.replace(
    /[٠١٢٣٤٥٦٧٨٩۰۱۲۳۴۵۶۷۸۹]/g,
    x => en[ar.indexOf(x)],
  )
  if (trimmedPhone.match(/^[^0+]/)) trimmedPhone = `0${trimmedPhone}`

  const justStartingPlus =
    trimmedPhone[0] + trimmedPhone.substring(1).replace(/\++/g, '')
  const normalized = justStartingPlus
    .replace(/[^\d+]+/g, '')
    .replace(/^00/, '+')
    .replace(/^0/, defaultCountryCode)

  // length is checked because of inputs like 'abc'
  if (!normalized.startsWith('+') || normalized.length !== 13) return ''
  return normalized
}

type ExtendedBatchSignupItem = BatchSignupItem & {
  inviterPhone?: string
  inviterId?: string
}

const rowToSignupItem = (row: any[]): ExtendedBatchSignupItem => {
  const [
    rawPhone,
    firstname,
    lastname,
    bio,
    username,
    nationalId,
    verified,

    hallIds,

    // profile
    gender,
    province,
    county,
    inviterPhone,
  ] = row.map(cell => cell?.toString().trim())
  return {
    user: {
      phone: normalizePhone(rawPhone),
      firstname,
      lastname,
      bio,
      username,
      nationalId: nationalId && nationalId.padStart(10, '0'),
      verified: !!verified,
      profileData: {
        gender: gender?.toUpperCase(),
        location: province || county ? { province, county } : undefined,
      },
    },
    hallIds: hallIds?.split('|').map(R.trim).filter(Boolean),
    inviterPhone,
  }
}

const getExpirationDate = (daysToExpire: number) => {
  if (!daysToExpire) return undefined

  const date = new Date()
  date.setDate(date.getDate() + daysToExpire)
  return date.toISOString()
}

const rowToCodeOptions = (
  row: any[],
  defaultOwnerId: string,
): FullCodeOptions => {
  const [ownerId, code, title, usageLimit, daysToExpire, tasks] = row.map(
    cell => cell?.toString().trim(),
  )
  return {
    code: code?.toUpperCase(),
    ownerId: ownerId || defaultOwnerId,
    title,
    expiresAt: daysToExpire ? getExpirationDate(+daysToExpire) : undefined,
    usageLimit: (usageLimit && +usageLimit) || undefined,
    tasks: tasks?.split('|').map(R.trim).filter(Boolean),
  }
}

const SectionHeader = ({ title }: { title: string }) => (
  <ListItem
    sx={{ justifyContent: 'center', backgroundColor: '#eeee', padding: 0 }}
  >
    <Typography fontWeight={700} fontSize={14}>
      {title}
    </Typography>
  </ListItem>
)

async function invitePhones(items: ExtendedBatchSignupItem[]) {
  const invitesByInviters = R.groupBy(
    R.prop('inviterId'),
    items.filter(R.prop('inviterId')),
  )
  console.log('invitesByInviters:', invitesByInviters)

  await Promise.all(
    Object.entries(invitesByInviters).map(async ([userId, invites]) => {
      const { payload } = await request<GetInvitations>(
        reqGetInvitations({ userId }),
      )
      if (!payload) return
      await request<SetUserInformation>(
        reqSetUserInformation({
          userId,
          capacity: payload.invitationLimit + invites.length,
        }),
      )
      await request<CreateInvitation>(
        reqCreateInvitation({
          userId,
          phoneNumber: invites.map(invite => invite.user.phone),
          canInvite: false,
        }),
      )
    }),
  )
}

export function UserProfileSuperUserActions({
  user,
}: {
  user: User | undefined
}) {
  const [invitationInfo, setInvitationInfo] =
    useState<ResGetInvitationsPayload | null>(null)
  const [refCodes, setRefCodes] = useState<FullCode[] | null>(null)
  useEffect(() => {
    if (user && hostConf.HAS_INVITATION) {
      request<GetReferrerCodes>(reqGetReferrerCodes({ userId: user.id }))
        .then(({ payload }) => setRefCodes(payload!.codes))
        .catch(console.log)
    }
  }, [user])

  const { id: myId } = useClientUser()

  if (!user) return null

  // const { id, verified } = user

  function handleInvitations({
    payload,
    error,
  }: ResponseAction<ResGetInvitationsPayload, string>) {
    if (error) {
      showErrorSnackbar(error.code)
      throw new Error(error.code)
    }

    setInvitationInfo(payload)
    return payload.invitationLimit
  }

  function getInvitationInfoText() {
    if (!invitationInfo) return '...'

    const { invitationLimit, invitationCount, accepted, pending } =
      invitationInfo
    return `${invitationCount}/${invitationLimit}, ${accepted.length} accepted, ${pending.length} pending`
  }

  const changeUserCapacity = async (capacity: number) => {
    const res = await request<SetUserInformation>(
      reqSetUserInformation({ userId: user.id, capacity }),
    )
    return handleInvitations(res)
  }
  const getUserCapacity = async () => {
    const res = await request<GetInvitations>(
      reqGetInvitations({ userId: user.id }),
    )
    return handleInvitations(res)
  }
  return (
    <>
      <SectionHeader title="This User Related Actions" />
      {hostConf.HAS_INVITATION ? (
        <NumberInputButton
          primaryText="Invitation Capacity"
          secondaryText={getInvitationInfoText()}
          getInitialValue={getUserCapacity}
          onClick={changeUserCapacity}
        />
      ) : null}
      {hostConf.HAS_INVITATION ? (
        <>
          <StyledListItem>
            <Stack direction="column" spacing={2} sx={{ width: '100%' }}>
              <Typography
                gutterBottom
                variant="body2"
                component="div"
                color="secondary"
                sx={{ marginTop: '5px' }}
              >
                Referrer Codes
              </Typography>
              {refCodes ? (
                !refCodes.length ? (
                  <p style={{ marginTop: 0, fontSize: 12 }}>
                    No referrer codes
                  </p>
                ) : (
                  refCodes.map(
                    ({ code, usage, usageLimit, revokedAt, expiresAt }) => (
                      <p
                        style={{ marginTop: 0, fontSize: 12 }}
                        color={
                          revokedAt || expiresAt || usage >= usageLimit
                            ? 'orange'
                            : 'semiblack'
                        }
                      >
                        <b>{code}: </b>
                        {usage} / {usageLimit || 'infinity'}
                      </p>
                    ),
                  )
                )
              ) : (
                'Loading...'
              )}
            </Stack>
          </StyledListItem>
          <StyledDivider />
        </>
      ) : null}

      <SectionHeader title="Global Actions" />
      {hostConf.HAS_INVITATION ? (
        <InputFileUpload
          primaryText="Create Ref Codes"
          sampleFileLink="https://docs.google.com/spreadsheets/d/1263uWPkAaKh3QrUUjVTgAGJyNuDcmgXNzzgNjIbe1ek/edit?usp=sharing"
          secondaryText="It's recommended to use codes that are long enough, unguessable, and bounded both by usage count and time."
          onProcess={wrapLoading(
            async (file): Promise<Draft<FullCodeOptions[]>> => {
              const sheets = await readSheetNames(file)
              const items = [] as FullCodeOptions[]

              const codeSet = new Set<string>()
              const invalidOwnerIdSet = new Set<string>()
              const duplicateSet = new Set<string>()
              for (const sheet of sheets) {
                // eslint-disable-next-line no-await-in-loop
                const rows = await readXlsxFile(file, { sheet })
                await Promise.all(
                  rows.slice(1).map(async row => {
                    const item = rowToCodeOptions(row, myId)
                    const { code, ownerId } = item
                    if (code && codeSet.has(code)) return duplicateSet.add(code)
                    const owner = await getUser(ownerId)
                    if (!owner) return invalidOwnerIdSet.add(ownerId)

                    items.push(item)
                    if (code) codeSet.add(code)
                  }),
                )
              }

              if (items.length > 1000) {
                return {
                  summary: 'No more than 1000 codes can be created at once',
                  error: true,
                  data: items,
                }
              }
              if (invalidOwnerIdSet.size) {
                return {
                  summary: ['Invalid OwnerIds:', ...invalidOwnerIdSet].join(
                    '\n',
                  ),
                  error: true,
                  data: items,
                }
              }
              if (duplicateSet.size) {
                return {
                  summary: ['Duplicate Codes:', ...duplicateSet].join('\n'),
                  data: items,
                  warning: true,
                }
              }
              return {
                summary: `${items.length} codes will be created.`,
                data: items,
              }
            },
          )}
          onConfirm={wrapLoading(
            async ({ data: items }: Draft<FullCodeOptions[]>) => {
              if (!items) return

              const res = await request<CreateReferrerCodes>(
                reqCreateReferrerCodes({ items }),
              )
              console.log(res)
              if (res.error) {
                const { code, message } = res.error
                return {
                  error: true,
                  summary: `${code}\n${JSON.stringify(message, null, 2)}`,
                }
              }
              return {
                error: false,
                summary: `Created ${res.payload.codes.length} codes.`,
              }
            },
          )}
        />
      ) : null}
      <InputFileUpload
        primaryText="Register Users"
        sampleFileLink="https://docs.google.com/spreadsheets/d/1a-fKZPv8muxYY-kKW6Lb-IPd1nArH2R0BAjV6WBENJg/edit?usp=sharing"
        onProcess={wrapLoading(
          async (file): Promise<Draft<ExtendedBatchSignupItem[]>> => {
            const sheets = await readSheetNames(file)
            const items: ExtendedBatchSignupItem[] = []
            const invalidPhones: string[] = []
            for (const sheet of sheets) {
              // eslint-disable-next-line no-await-in-loop
              const rows = await readXlsxFile(file, { sheet })
              for (const row of rows.slice(1)) {
                const item = rowToSignupItem(row)
                if (item.user.phone) items.push(item)
                else invalidPhones.push(row[0])
              }
            }

            const invalidInviterPhones: string[] = []
            const unregisteredInviterPhones: string[] = []
            if (hostConf.HAS_INVITATION) {
              const inviterPhoneSet = new Set<string>()
              items.forEach(item => {
                if (!item.inviterPhone) return

                const phone = normalizePhone(item.inviterPhone)

                if (!phone) invalidInviterPhones.push(item.inviterPhone)
                else inviterPhoneSet.add(phone)

                item.inviterPhone = phone
              })
              const inviterPhones = Array.from(inviterPhoneSet)
              const inviters = await Promise.all(
                inviterPhones.map(serverGetUserByPhone),
              )
              const inviterPhoneToId = R.zipObj(
                inviterPhones,
                R.pluck('id', inviters),
              )

              items.forEach(item => {
                const { inviterPhone } = item
                if (!inviterPhone) return

                const inviterId = inviterPhoneToId[inviterPhone]
                if (!inviterId) unregisteredInviterPhones.push(inviterPhone)
                // eslint-disable-next-line no-param-reassign
                else item.inviterId = inviterId
              })
            }
            const summary = [`${items.length} users will be registered.`]
            if (invalidPhones.length) {
              summary.push('** Invalid Phones **', ...invalidPhones)
            }
            if (invalidInviterPhones.length) {
              summary.push(
                '** Invalid Inviter Phones **',
                ...invalidInviterPhones,
              )
            }
            if (unregisteredInviterPhones.length) {
              summary.push(
                '** Unregistered Inviters **',
                ...unregisteredInviterPhones,
              )
            }
            if (summary.length > 1) {
              return {
                summary: summary.join('\n'),
                data: items,
                warning: true,
              }
            }
            return {
              summary: summary[0],
              data: items,
            }
          },
        )}
        onConfirm={wrapLoading(
          async ({ data }: Draft<ExtendedBatchSignupItem[]>) => {
            if (!data) return

            await invitePhones(data)
            getUserCapacity()

            let hadError = false
            const stats: ResBatchSignupPayload['stats'] = {
              invalidPhones: 0,
              added: 0,
              existed: 0,
            }
            for (const items of R.splitEvery(1000, data)) {
              const res = await request<BatchSignup>(
                reqBatchSignup({
                  items,
                  // @ts-expect-error type-mismatch
                  dryRun: false,
                }),
              )
              console.log(res)
              if (!res.payload) {
                hadError = true
              } else {
                stats.invalidPhones += res.payload.stats.invalidPhones
                stats.added += res.payload.stats.added
                stats.existed += res.payload.stats.existed
              }
            }
            if (hadError) {
              // TODO handle duplicate_username error
              // TODO handle duplicate_nationalId error
              return {
                error: true,
                summary: 'Some or all failed',
              }
            }
            return {
              error: false,
              summary: JSON.stringify(stats, null, 2),
            }
          },
        )}
      />
      {/* <SwitchButtonItem
        label={t('common.verified')}
        checked={verified}
        onClick={() => updateUser(id, { verified: !verified })}
      /> */}
    </>
  )
}

const StyledListItem = styled(ListItem)(({ theme }) => ({
  width: '100%',
  minHeight: '66px',

  margin: 0,
  padding: '8px 15px',

  outline: 'none',
  justifyContent: 'space-between',
  textAlign: 'left',
  backgroundColor: theme.palette.white,
  flexGrow: 0,
}))

const StyledDivider = styled(Divider)`
  &:last-child {
    display: none;
  }
`

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
})

type Draft<D> = {
  summary: string
  data: D | null
  warning?: boolean
  error?: boolean
}

type Result = {
  summary: string
  error: boolean
}

interface InputFileUploadProps<D> {
  primaryText: string
  secondaryText?: string
  sampleFileLink: string
  onProcess: (file: File) => Promise<Draft<D>>
  onConfirm: (draft: Draft<D>) => Promise<Result>
  disabled?: boolean
}

function InputFileUpload<D>({
  primaryText,
  secondaryText,
  sampleFileLink,
  onProcess,
  onConfirm,
  disabled,
}: Readonly<InputFileUploadProps<D>>) {
  const [draft, setDraft] = useState<Draft<D> | null>(null)
  const [result, setResult] = useState<Result | null>(null)
  const [force, setForce] = useState(false)
  return (
    <>
      <StyledListItem>
        <Stack direction="column" spacing={2} sx={{ width: '100%' }}>
          <Box>
            <Typography
              gutterBottom
              variant="body2"
              component="div"
              color="secondary"
              sx={{ marginTop: '5px' }}
            >
              {primaryText}
            </Typography>
            <Typography
              dir="auto"
              variant="caption"
              sx={{ margin: '5px 0' }}
              color="semiBlack"
            >
              [
              <Link target="_blank" rel="noreferrer" href={sampleFileLink}>
                Sample File
              </Link>
              ] All sheets are read. First row is considered header and ignored.
            </Typography>
            <br />
            {secondaryText ? (
              <Typography
                dir="auto"
                variant="caption"
                sx={{ margin: '5px 0', whiteSpace: 'pre-line' }}
                color="semiBlack"
              >
                {secondaryText}
              </Typography>
            ) : null}
          </Box>
          <Button
            disabled={disabled}
            variant="outlined"
            size="small"
            component="label"
            role={undefined}
            tabIndex={-1}
          >
            Upload Excel file
            <VisuallyHiddenInput
              disabled={disabled}
              type="file"
              accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              onChange={event => {
                setDraft(null)
                setResult(null)
                setForce(false)
                const file = event.target.files[0]
                if (file) onProcess(file).then(setDraft)
              }}
            />
          </Button>
          {draft ? (
            <>
              <Typography
                dir="auto"
                component="code"
                variant="caption"
                sx={{
                  margin: '5px',
                  whiteSpace: 'pre-line',
                  userSelect: 'text',
                }}
                color={
                  draft.error ? 'red' : draft.warning ? 'orange' : 'semiBlack'
                }
              >
                <b>
                  {draft.error ? 'ERROR\n' : draft.warning ? 'WARNING\n' : ''}
                </b>
                {draft.summary}
              </Typography>
              <Button
                variant="contained"
                size="small"
                onClick={() => onConfirm(draft).then(setResult)}
                disabled={disabled || draft.error || (draft.warning && !force)}
              >
                Send
              </Button>
              {draft.warning ? (
                <FormControlLabel
                  label="Ignore warnings"
                  dir="ltr"
                  sx={{ marginTop: '0 !important' }}
                  control={
                    <Checkbox
                      checked={force}
                      onChange={() => setForce(!force)}
                    />
                  }
                />
              ) : null}
              {result ? (
                <Typography
                  dir="auto"
                  component="code"
                  variant="caption"
                  sx={{
                    margin: '5px',
                    whiteSpace: 'pre-line',
                    userSelect: 'text',
                  }}
                  color={result.error ? 'red' : 'semiBlack'}
                >
                  {result.summary}
                </Typography>
              ) : null}
            </>
          ) : null}
        </Stack>
      </StyledListItem>
      <StyledDivider />
    </>
  )
}

type NumberInputButtonProps = {
  primaryText: string
  secondaryText?: string
  getInitialValue: () => Promise<number>
  onClick: (value: number) => Promise<number>
}

export function NumberInputButton({
  primaryText,
  secondaryText,
  onClick,
  getInitialValue,
}: Readonly<NumberInputButtonProps>) {
  const [value, setValue] = useState(0)
  const [disabled, setDisabled] = useState(true)
  const [initial, setInitial] = useState(0)
  const dirty = value !== initial

  useEffect(() => {
    getInitialValue()
      .then(val => {
        setValue(val)
        setInitial(val)
        setDisabled(false)
      })
      .catch(console.log)
  }, [getInitialValue])

  return (
    <>
      <StyledListItem>
        <Stack direction="column" spacing={2} sx={{ width: '100%' }}>
          <Typography
            gutterBottom
            variant="body2"
            component="div"
            color="secondary"
            sx={{ marginTop: '5px' }}
          >
            {primaryText}
          </Typography>
          <Typography
            dir="auto"
            variant="caption"
            sx={{ margin: '0 0 !important' }}
            color="semiBlack"
          >
            {secondaryText}
          </Typography>
          <Stack
            direction="row-reverse"
            justifyContent="center"
            alignItems="center"
            sx={{ marginTop: '5px !important' }}
          >
            <Box sx={{ width: '50%' }}>
              <NumberInput
                direction="ltr"
                disabled={disabled}
                value={disabled ? '' : value.toString()}
                onChange={setValue}
              />
            </Box>
            <Button
              sx={{ marginLeft: 2 }}
              variant="contained"
              disabled={disabled || !dirty}
              onClick={() => {
                setDisabled(true)
                onClick(value)
                  .then(setValue)
                  .catch(console.log)
                  .finally(() => setDisabled(false))
              }}
            >
              Update
            </Button>
          </Stack>
        </Stack>
      </StyledListItem>
      <StyledDivider />
    </>
  )
}
